import type {
  ColumnDef,
  Row,
  Table as TableTanStack,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  ArrowRight,
  Building2,
  Calendar,
  DollarSign,
  Eye,
  FileText,
} from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import { cn } from 'lib/utils';

import type { IStoreIncomingMasterList } from '../../../api/IncomingInvoice.interface';

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement> & { wrapperClassName?: string }
>(({ className, wrapperClassName, ...props }, ref) => (
  <div className={cn('relative w-full overflow-auto', wrapperClassName)}>
    <table
      ref={ref}
      className={cn('w-full caption-bottom text-sm', className)}
      {...props}
    />
  </div>
));
Table.displayName = 'Table';

type Props = {
  items: IStoreIncomingMasterList[];
  table: TableTanStack<IStoreIncomingMasterList>;
  columns: ColumnDef<IStoreIncomingMasterList>[];
  tableContainerRef: React.RefObject<HTMLDivElement>;
  resetFilters: () => void;
  selectedInvoice: number | null;
  setSelectedInvoice: (invoiceId: number) => void;
};

function TableBody({
  items,
  table,

  tableContainerRef,
  resetFilters,
  selectedInvoice,
  setSelectedInvoice,
}: Props) {
  const { rows } = table.getRowModel();

  // Important: Keep the row virtualizer in the lowest component possible to avoid unnecessary re-renders.
  const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
    count: rows.length,
    estimateSize: () => 80, //estimate row height for accurate scrollbar dragging
    getScrollElement: () => tableContainerRef.current,
    //measure dynamic row height, except in firefox because it measures table border height incorrectly
    measureElement:
      typeof window !== 'undefined' &&
      navigator.userAgent.indexOf('Firefox') === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();

  const paddingTop =
    virtualRows && virtualRows.length > 0 ? virtualRows[0]?.start || 0 : 0;
  const paddingBottom =
    virtualRows && virtualRows.length > 0
      ? rowVirtualizer.getTotalSize() -
        (virtualRows[virtualRows.length - 1]?.end || 0)
      : 0;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-EG', {
      style: 'currency',
      currency: 'EGP',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-EG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="relative w-full">
      {items && items.length > 0 ? (
        <div
          ref={tableContainerRef}
          className="h-[70vh] w-full overflow-auto rounded-2xl border border-slate-200/50 bg-white/50 backdrop-blur-sm dark:border-slate-700/50 dark:bg-slate-800/50"
        >
          <Table className="relative w-full">
            <thead className="sticky top-0 z-20 bg-gradient-to-r from-slate-50 to-slate-100 shadow-xl dark:from-slate-800 dark:to-slate-900">
              <tr className="border-b border-slate-200/50 dark:border-slate-700/50">
                <th className="h-20 w-32 border-r border-slate-200/50 bg-gradient-to-br from-indigo-50 to-purple-50 px-6 py-4 text-center text-lg font-bold text-slate-700 shadow-sm dark:border-slate-700/50 dark:from-indigo-950/30 dark:to-purple-950/30 dark:text-slate-300">
                  <div className="flex items-center justify-center gap-2">
                    <FileText className="h-5 w-5" />
                    رقم الفاتورة
                  </div>
                </th>
                <th className="h-20 w-80 border-r border-slate-200/50 bg-gradient-to-br from-emerald-50 to-teal-50 px-6 py-4 text-center text-lg font-bold text-slate-700 shadow-sm dark:border-slate-700/50 dark:from-emerald-950/30 dark:to-teal-950/30 dark:text-slate-300">
                  <div className="flex items-center justify-center gap-2">
                    <Building2 className="h-5 w-5" />
                    معلومات المورد
                  </div>
                </th>
                <th className="h-20 w-48 border-r border-slate-200/50 bg-gradient-to-br from-amber-50 to-orange-50 px-6 py-4 text-center text-lg font-bold text-slate-700 shadow-sm dark:border-slate-700/50 dark:from-amber-950/30 dark:to-orange-950/30 dark:text-slate-300">
                  <div className="flex items-center justify-center gap-2">
                    <Calendar className="h-5 w-5" />
                    تاريخ الفاتورة
                  </div>
                </th>
                <th className="h-20 w-64 border-r border-slate-200/50 bg-gradient-to-br from-fuchsia-50 to-pink-50 px-6 py-4 text-center text-lg font-bold text-slate-700 shadow-sm dark:border-slate-700/50 dark:from-fuchsia-950/30 dark:to-pink-950/30 dark:text-slate-300">
                  <div className="flex items-center justify-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    إجمالي الفاتورة
                  </div>
                </th>
                <th className="h-20 w-48 border-r border-slate-200/50 bg-gradient-to-br from-blue-50 to-indigo-50 px-6 py-4 text-center text-lg font-bold text-slate-700 shadow-sm dark:border-slate-700/50 dark:from-blue-950/30 dark:to-indigo-950/30 dark:text-slate-300">
                  <div className="flex items-center justify-center gap-2">
                    <Eye className="h-5 w-5" />
                    الإجراءات
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {paddingTop > 0 && (
                <tr>
                  <td style={{ height: `${paddingTop}px` }} />
                </tr>
              )}
              {virtualRows &&
                virtualRows.map((virtualRow: any) => {
                  const row = rows[
                    virtualRow.index
                  ] as Row<IStoreIncomingMasterList>;
                  const isSelected = selectedInvoice === row.original.ID;

                  return (
                    <tr
                      key={row.id}
                      data-index={virtualRow.index}
                      ref={rowVirtualizer.measureElement}
                      className={cn(
                        'group cursor-pointer border-b border-slate-100 transition-all duration-300 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 hover:shadow-lg dark:border-slate-700/50 dark:hover:from-blue-950/30 dark:hover:to-indigo-950/30',
                        isSelected &&
                          'bg-gradient-to-r from-blue-100 to-indigo-100 shadow-xl dark:from-blue-900/50 dark:to-indigo-900/50',
                        selectedInvoice === row.original.ID &&
                          'bg-gradient-to-r from-emerald-100 to-teal-100 shadow-2xl dark:from-emerald-900/50 dark:to-teal-900/50',
                      )}
                      onClick={() => setSelectedInvoice(row.original.ID)}
                    >
                      {/* Invoice Number */}
                      <td className="h-20 border-r border-slate-100 px-6 py-4 text-center dark:border-slate-700/50">
                        <div className="flex items-center justify-center gap-3">
                          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-md">
                            <FileText className="h-6 w-6 text-white" />
                          </div>
                          <div>
                            <div className="text-xl font-bold text-slate-900 dark:text-slate-100">
                              #{row.original.ID}
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                              فاتورة وارد
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Supplier Info */}
                      <td className="h-20 border-r border-slate-100 px-6 py-4 text-center dark:border-slate-700/50">
                        <div className="flex items-center justify-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 shadow-md">
                            <Building2 className="h-5 w-5 text-white" />
                          </div>
                          <div className="text-right">
                            <div className="text-base font-semibold text-slate-900 dark:text-slate-100">
                              {row.original.SupplierName}
                            </div>
                            <div className="text-sm text-slate-600 dark:text-slate-400">
                              {row.original.EmployeeName}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Date */}
                      <td className="h-20 border-r border-slate-100 px-6 py-4 text-center dark:border-slate-700/50">
                        <div className="flex items-center justify-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 shadow-md">
                            <Calendar className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                              {formatDate(row.original.TrDate)}
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                              تاريخ الشراء
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Amount */}
                      <td className="h-20 border-r border-slate-100 px-6 py-4 text-center dark:border-slate-700/50">
                        <div className="flex items-center justify-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-fuchsia-500 to-pink-600 shadow-md">
                            <DollarSign className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <div className="text-lg font-bold text-slate-900 dark:text-slate-100">
                              {formatCurrency(row.original.TotalInvoice)}
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                              إجمالي المبلغ
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="h-20 px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-3">
                          <div className="flex items-center gap-2 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 px-4 py-2 dark:from-blue-950/30 dark:to-indigo-950/30">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 shadow-md">
                              <Eye className="h-4 w-4 text-white" />
                            </div>
                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                              عرض التفاصيل
                            </span>
                          </div>
                          <ArrowRight className="h-5 w-5 text-slate-400 transition-transform group-hover:translate-x-1" />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              {paddingBottom > 0 && (
                <tr>
                  <td style={{ height: `${paddingBottom}px` }} />
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-200/50 bg-white/50 p-12 shadow-lg backdrop-blur-sm dark:border-slate-700/50 dark:bg-slate-800/50">
          <div className="relative mb-6">
            <div className="relative flex h-24 w-24 items-center justify-center">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-slate-400 to-slate-600 opacity-20 blur-xl"></div>
              <div className="relative flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-500 to-slate-700 shadow-lg">
                <FileText className="h-10 w-10 text-white" />
              </div>
            </div>
          </div>
          <h3 className="mb-2 text-xl font-semibold text-slate-900 dark:text-slate-100">
            لا توجد بيانات
          </h3>
          <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
            لم يتم العثور على فواتير تطابق معايير البحث
          </p>
          <Button
            variant="outline"
            onClick={resetFilters}
            className="group relative overflow-hidden border-slate-300 bg-white/50 text-slate-700 shadow-sm transition-all duration-300 hover:bg-slate-50 hover:shadow-md dark:border-slate-600 dark:bg-slate-800/50 dark:text-slate-300 dark:hover:bg-slate-700/50"
          >
            <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-white/0 via-white/20 to-white/0 transition-transform duration-700 group-hover:translate-x-full"></div>
            إعادة تطبيق الفلتر
          </Button>
        </div>
      )}
    </div>
  );
}

export default TableBody;
